import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { generatePurchaseOrderHTML, PurchaseOrderTemplateData } from "../templates/purchaseOrderPdfTemplate";
import { formatPhoneNumber } from "../_common/functions";

export interface PdfGenerationOptions {
  downloadOnly?: boolean;
  fileName?: string;
}

export class PdfGenerator {
  private static createTemporaryElement(htmlContent: string): HTMLDivElement {
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.top = "0";
    tempDiv.style.width = "800px";
    tempDiv.style.backgroundColor = "#ffffff";
    tempDiv.style.padding = "20px";
    tempDiv.style.fontFamily = "Arial, sans-serif";
    
    tempDiv.innerHTML = htmlContent;
    document.body.appendChild(tempDiv);
    
    return tempDiv;
  }

  private static removeTemporaryElement(element: HTMLDivElement): void {
    if (document.body.contains(element)) {
      document.body.removeChild(element);
    }
  }

  private static async createCanvas(element: HTMLDivElement): Promise<HTMLCanvasElement> {
    const contentHeight = element.scrollHeight + 40; // Add padding
    
    return new Promise((resolve, reject) => {
      let animationFrameId: number | null = null;
      let isResolved = false;
      
      // Cleanup function
      const cleanup = () => {
        if (animationFrameId && !isResolved) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      };
      
      // Use requestAnimationFrame for better timing
      animationFrameId = requestAnimationFrame(async () => {
        if (isResolved) return; // Prevent execution if already resolved/rejected
        
        try {
          const canvas = await html2canvas(element, {
            scale: 1.5,
            backgroundColor: "#ffffff",
            width: 800,
            height: contentHeight,
            useCORS: true,
            allowTaint: true,
            ignoreElements: (element) => {
              // Ignore any elements with modern CSS that might cause issues
              const style = window.getComputedStyle(element);
              return (
                style.color.includes("oklch") ||
                style.backgroundColor.includes("oklch") ||
                style.borderColor.includes("oklch")
              );
            },
            onclone: (clonedDoc) => {
              // Additional cleanup on the cloned document
              const allElements = clonedDoc.querySelectorAll("*");
              allElements.forEach((el: any) => {
                // Force override any remaining problematic styles
                if (el.style) {
                  el.style.setProperty("color", "#000000", "important");
                  el.style.setProperty("background-color", "#ffffff", "important");
                  el.style.setProperty("border-color", "#cccccc", "important");
                }
              });
            },
          });
          
          isResolved = true;
          cleanup();
          resolve(canvas);
        } catch (error) {
          isResolved = true;
          cleanup();
          reject(error);
        }
      });
      
      // Handle potential Promise cancellation (though not directly supported in standard Promises)
      // This ensures cleanup happens if the Promise executor is somehow interrupted
      setTimeout(() => {
        if (!isResolved) {
          cleanup();
        }
      }, 30000); // 30 second timeout as safety net
    });
  }

  private static createPdfFromCanvas(canvas: HTMLCanvasElement): jsPDF {
    const pdf = new jsPDF("p", "mm", "a4");
    const imgData = canvas.toDataURL("image/png", 1.0);
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // Calculate dimensions to fit the PDF page
    const ratio = Math.min(
      pdfWidth / (imgWidth * 0.264583),
      pdfHeight / (imgHeight * 0.264583)
    );
    const finalWidth = imgWidth * 0.264583 * ratio;
    const finalHeight = imgHeight * 0.264583 * ratio;

    const imgX = (pdfWidth - finalWidth) / 2;
    const imgY = 10; // Small top margin

    pdf.addImage(imgData, "PNG", imgX, imgY, finalWidth, finalHeight);
    return pdf;
  }

  public static async generatePurchaseOrderPdf(
    data: PurchaseOrderTemplateData,
    options: PdfGenerationOptions = {},
    language?: string
  ): Promise<{ blob: Blob; fileName: string; pdf: jsPDF }> {
    const { fileName = `${data.orderNumber ?? "Draft"}.pdf` } = options;

    return new Promise(async (resolve, reject) => {
      let tempDiv: HTMLDivElement | null = null;

      try {
        // Generate HTML content from template
        const htmlContent = generatePurchaseOrderHTML(data, language);
        
        // Create temporary element
        tempDiv = this.createTemporaryElement(htmlContent);
        
        // Create canvas from element
        const canvas = await this.createCanvas(tempDiv);
        
        // Remove temporary element
        this.removeTemporaryElement(tempDiv);
        tempDiv = null;
        
        // Create PDF from canvas
        const pdf = this.createPdfFromCanvas(canvas);
        
        // Convert PDF to Blob
        const pdfBlob = pdf.output("blob");
        
        resolve({
          blob: pdfBlob,
          fileName,
          pdf
        });
        
      } catch (error) {
        // Clean up on error
        if (tempDiv) {
          this.removeTemporaryElement(tempDiv);
        }
        reject(error);
      }
    });
  }

  public static async downloadPdf(
    data: PurchaseOrderTemplateData,
    fileName?: string,
    language?: string
  ): Promise<void> {
    try {
      const { pdf, fileName: generatedFileName } = await this.generatePurchaseOrderPdf(data, { fileName }, language);
      pdf.save(generatedFileName);
    } catch (error) {
      throw new Error(`Failed to download PDF: ${error}`);
    }
  }

  public static async generatePdfBlob(
    data: PurchaseOrderTemplateData,
    language?: string
  ): Promise<{ blob: Blob; fileName: string }> {
    try {
      const { blob, fileName } = await this.generatePurchaseOrderPdf(data, {}, language);
      return { blob, fileName };
    } catch (error) {
      throw new Error(`Failed to generate PDF blob: ${error}`);
    }
  }
}

// Utility function to transform purchase order data to template format
export const transformPurchaseOrderData = (purchaseOrderData: any): PurchaseOrderTemplateData => {
  const data = purchaseOrderData.data;
  
  return {
    orderNumber: data.orderNumber || `PO-${data.purchaseOrderId}`,
    userName: data.userName || data.displayName || "N/A",
    confirmationNumber: data.confirmationNumber || "Not Available",
    //if ghost vendor show N/A
    vendorName: data.fkVendorId === "NTM=" ? "Unassigned" : data.vendorName || "N/A",
    primaryPhone: formatPhoneNumber(data.primaryPhone) || "N/A",
    primaryEmailAddress: data.primaryEmailAddress || "N/A",
    createDate: data.createDate || "",
    locationName: data.locationName || "Not Available",
    expectedDeliveryDate: data.expectedDeliveryDate || "",
    note: data.note || "-",
    website: data.website || "Not Available",
    products: (data.products || []).map((product: any) => ({
      productName: product.productName || "",
      orderBoxQuantity: product.orderBoxQuantity || 0,
      vendorPerBoxValue: product.vendorPerBoxValue || 1,
      boxCost: product.boxCost || 0,
      totalCost: product.totalCost || 0,
      note: product.note || "-"
    })),
    subTotal: data.subTotal || 0,
    tax: data.tax || 0,
    fees: data.fees || 0,
    totalDue: data.totalDue || 0
  };
};